import { FORM_FIELDS_WITH_SIGNER } from '@/constants/constants';
import {
  IPlaceholderField,
  ISignerField,
} from '@/interfaces/placeholder-field';
import { areArraysEqual } from '@/utils/utils';

export class SigningUtils {
  static getUniqueSigners(signerFields: ISignerField[]): ISignerField[] {
    const uniqueSignersMap = new Map<string, ISignerField>();

    signerFields.forEach((signerField) => {
      if (!signerField) return;
      const key = signerField.userId?.toString() || signerField.emailColumnId;
      if (uniqueSignersMap.has(key)) return;
      uniqueSignersMap.set(key, signerField);
    });

    return Array.from(uniqueSignersMap.values());
  }

  static extractSignatureFields(
    fileDetails: { fields?: IPlaceholderField[] }[],
  ) {
    return fileDetails.flatMap(
      (fileDetail) =>
        fileDetail.fields?.filter((field) => {
          if (FORM_FIELDS_WITH_SIGNER.includes(field.itemId)) return true;
          return false;
        }) || [],
    );
  }

  static extractUniqueSigners(
    fileDetails: { fields: IPlaceholderField[] }[],
  ): ISignerField[] {
    return this.getUniqueSigners(
      this.extractSignatureFields(fileDetails)
        .filter((field) => field.signer)
        .map((field) => field.signer),
    );
  }

  static hasSignersChanged(
    sourceSigners: ISignerField[],
    targetSigners: ISignerField[],
  ) {
    const sourceSignerFieldIds = sourceSigners.map(
      (signer) => signer.userId?.toString() || signer.emailColumnId,
    );
    const targetSignerFieldIds = targetSigners.map(
      (signer) => signer.userId?.toString() || signer.emailColumnId,
    );

    return !areArraysEqual(sourceSignerFieldIds, targetSignerFieldIds);
  }

  /**
   * Extracts the identifier params ({ emailColumnId } or { userId }) from a signer object
   * for use in OTP send/verify API calls.
   */
  static getSignerIdentifierParams(
    signer: ISignerField,
  ): { emailColumnId?: string; userId?: string } {
    if (signer.emailColumnId) return { emailColumnId: signer.emailColumnId };
    if (signer.userId) return { userId: signer.userId.toString() };
    return {};
  }

  /**
   * Creates an object with either emailColumnId or userId based on the identifier type.
   * This utility is used to conditionally spread email identifier properties in API calls.
   *
   * @param identifierType - The type of identifier: 'emailColumnId' | 'userId' | null
   * @param identifier - The identifier value (string or null)
   * @param signerEmail - The signer's email address (optional)
   * @param isEmailAddedBySigner - Whether the email was added by the signer (optional)
   * @returns An object with either { emailColumnId: string } or { userId: string } or {},
   *          plus { emailAddress: signerEmail } if both signerEmail and isEmailAddedBySigner exist
   *
   */
  static buildEmailQueryParamsObject(
    identifierType: 'emailColumnId' | 'userId' | null | undefined,
    identifier: string | null | undefined,
    signerEmail?: string | null,
    isEmailAddedBySigner?: boolean,
  ): { emailColumnId?: string; userId?: string; emailAddress?: string } {
    return {
      ...(identifier &&
        identifierType === 'emailColumnId' && { emailColumnId: identifier }),
      ...(identifier && identifierType === 'userId' && { userId: identifier }),
      ...(signerEmail && isEmailAddedBySigner && { emailAddress: signerEmail }),
    };
  }
}
